import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { AuthSelector, CORE_ROUTE, CoreState } from "@obg/ngx.core";
import { PlatformService } from "@obg/ngx.global.evo";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { EMPTY, Observable, of } from "rxjs";
import { filter, map, pairwise, switchMap, take } from "rxjs/operators";

import { GeoFencingAction } from "./geofencing.action";
import { ONTARIO_BLOCKED_ROUTES } from "./geofencing.const";
import { GeofencingExperimentService } from "./geofencing.experiment.service";
import { CountryAreaStatus } from "./geofencing.model";
import { GeofencingSelector } from "./geofencing.selector";
import { GeofencingService } from "./geofencing.service";

@Injectable({
	providedIn: "root",
})
export class AwsGeofencingService {
	private afterLogin$ = this.store.select(AuthSelector.isAuthenticated).pipe(
		pairwise(),
		filter(([prev, next]) => !prev && next)
	);

	constructor(
		private store: Store<CoreState>,
		private platformService: PlatformService,
		private geofencingService: GeofencingService,
		private experimentService: GeofencingExperimentService
	) {}

	initialize$() {
		return this.afterLogin$.pipe(
			switchMap(() => this.store.select(RouteSelector.getCurrent)),
			switchMap((currentRoute) => (this.isBlockedRoute(currentRoute?.name) ? this.isAccessAllowed$() : EMPTY))
		);
	}

	isAccessAllowed$() {
		this.store.dispatch(
			GeoFencingAction.setWageringBlockDialogEnabled(this.geofencingService.isWageringBlockDialogEnabled())
		);

		return this.store.select(GeofencingSelector.countryAreaStatus).pipe(
			switchMap((countryAreaStatus) => {
				if (
					this.platformService.isServer ||
					!this.geofencingService.isAwsLocationServiceEnabled() ||
					countryAreaStatus === CountryAreaStatus.Valid
				) {
					return of(true);
				}

				return this.isIpLocationRestricted$().pipe(
					take(1),
					switchMap((isIpLocationRestricted) => (isIpLocationRestricted ? this.isLocationValid$() : of(true)))
				);
			})
		);
	}

	private isLocationValid$(): Observable<boolean> {
		this.store.dispatch(GeoFencingAction.validateLocation());

		return this.store.select(GeofencingSelector.isBusy).pipe(
			filter((isBusy) => !isBusy),
			switchMap(() => this.store.select(GeofencingSelector.isValid)),
			ignoreNil(),
			map((isValid) => {
				if (!isValid) {
					this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));
				}
				return isValid;
			})
		);
	}

	private isIpLocationRestricted$(): Observable<boolean> {
		if (this.experimentService.isGeofencingIpLocationCheckDisabled()) {
			return of(true);
		}

		this.store.dispatch(GeoFencingAction.getAwsIpLocation());

		return this.store.select(GeofencingSelector.isBusy).pipe(
			filter((isBusy) => !isBusy),
			switchMap(() => this.store.select(GeofencingSelector.countryAreaStatus)),
			map((status) => status === CountryAreaStatus.Restricted),
			ignoreNil()
		);
	}

	private isBlockedRoute(routeName?: string): boolean {
		return (
			!!routeName && ONTARIO_BLOCKED_ROUTES.some((route) => routeName.toLowerCase().includes(route.toLowerCase()))
		);
	}
}
